import { useCallback, useMemo, useState } from 'react';
import { IBranchTransferExecuteValues } from '../../interfaces';
import { BRANCH_TRANSFER_LOCAL_WAREHOUSE_STATE_ID } from '../../constants';
import { useBranchTransferPreview } from './use-branch-transfer-preview';

export const useBranchTransferForm = (fromBranchId: number) => {
  const [toBranchId, setToBranchIdState] = useState<number>();
  const [selectedStateIds, setSelectedStateIds] = useState<number[]>();
  const [containerId, setContainerId] = useState<number>();
  const [touched, setTouched] = useState(false);

  const previewQuery = useBranchTransferPreview(fromBranchId, toBranchId);
  const preview = previewQuery.data;

  const byState = useMemo(() => preview?.declarations.byState || [], [preview]);
  const allStateIds = useMemo(() => byState.map((state) => state.stateId), [byState]);
  const stateIds = selectedStateIds ?? allStateIds;

  const setToBranchId = useCallback((value: number) => {
    setToBranchIdState(value);
    setSelectedStateIds(undefined);
    setContainerId(undefined);
    setTouched(false);
  }, []);

  const setStateIds = useCallback((values: number[]) => setSelectedStateIds(values), []);
  const toggleAllStates = useCallback((checked: boolean) => setSelectedStateIds(checked ? allStateIds : []), [allStateIds]);

  const selectedCount = byState.filter((state) => stateIds.includes(state.stateId)).reduce((sum, state) => sum + state.count, 0);
  const allStatesChecked = byState.length > 0 && stateIds.length === byState.length;
  const someStatesChecked = stateIds.length > 0 && !allStatesChecked;

  const containerRequired = !!preview && preview.declarations.localWarehouse > 0 && stateIds.includes(BRANCH_TRANSFER_LOCAL_WAREHOUSE_STATE_ID);
  const containers = preview?.containers || [];

  const errors = {
    toBranchId: touched && !toBranchId ? 'Yeni filialı seçin' : undefined,
    containerId: touched && containerRequired && !containerId ? 'Yeşik seçimi məcburidir' : undefined,
  };

  /** Marks the form as touched and returns the request payload, or `null` while it is invalid. */
  const validate = useCallback((): IBranchTransferExecuteValues | null => {
    setTouched(true);
    if (!toBranchId || !preview || (containerRequired && !containerId)) return null;
    return { fromBranchId, toBranchId, stateIds, containerId: containerRequired ? (containerId ?? null) : null };
  }, [containerId, containerRequired, fromBranchId, preview, stateIds, toBranchId]);

  return {
    previewQuery,
    preview,
    toBranchId,
    setToBranchId,
    byState,
    stateIds,
    setStateIds,
    toggleAllStates,
    allStatesChecked,
    someStatesChecked,
    selectedCount,
    containers,
    containerRequired,
    containerId,
    setContainerId,
    errors,
    validate,
  };
};
